import { useState, useEffect, useCallback, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { WALKTHROUGH_STEPS } from "./walkthrough-steps";
import { WalkthroughOverlay } from "./WalkthroughOverlay";
import { WalkthroughTooltip } from "./WalkthroughTooltip";

/** Find the first visible element matching a data-walkthrough attribute */
function findTarget(walkthroughId: string): HTMLElement | null {
  const elements = document.querySelectorAll<HTMLElement>(
    `[data-walkthrough="${walkthroughId}"]`
  );
  for (const el of elements) {
    if (el.offsetWidth > 0 && el.offsetHeight > 0) return el;
  }
  return null;
}

/** Wait for an element to appear in the DOM (after navigation) */
function waitForElement(
  walkthroughId: string,
  timeoutMs = 3000
): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const el = findTarget(walkthroughId);
    if (el) return resolve(el);

    const start = Date.now();
    const check = () => {
      const el = findTarget(walkthroughId);
      if (el) return resolve(el);
      if (Date.now() - start > timeoutMs) return resolve(null);
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

/** Elevate the sticky nav header above the walkthrough overlay */
function setNavElevation(elevated: boolean) {
  const header = document.querySelector("header");
  if (!header) return;
  if (elevated) {
    (header as HTMLElement).style.zIndex = "70";
  } else {
    (header as HTMLElement).style.removeProperty("z-index");
  }
}

export function WalkthroughProvider({ children }: { children: ReactNode }) {
  const { preferences, setWalkthroughCompleted } = useUserPreferences();
  const navigate = useNavigate();
  const location = useLocation();

  const [phase, setPhase] = useState<"idle" | "prompt" | "active" | "completed">("idle");
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [prevTargetEl, setPrevTargetEl] = useState<HTMLElement | null>(null);
  const [stepReady, setStepReady] = useState(false); // hide UI until target is resolved

  const active = phase === "active";

  // Reset phase when walkthroughCompleted is explicitly set to false (e.g. Replay Tutorial)
  useEffect(() => {
    if (preferences.walkthroughCompleted === false && phase === "completed") {
      setPhase("idle");
    }
  }, [preferences.walkthroughCompleted, phase]);

  // Auto-trigger the welcome prompt after onboarding completes
  // Only triggers when walkthroughCompleted is explicitly false (not undefined/missing)
  useEffect(() => {
    if (
      preferences.onboardingCompleted &&
      preferences.walkthroughCompleted === false &&
      phase === "idle"
    ) {
      const timer = setTimeout(() => setPhase("prompt"), 800);
      return () => clearTimeout(timer);
    }
  }, [preferences.onboardingCompleted, preferences.walkthroughCompleted, phase]);

  // Elevate nav when walkthrough is active
  useEffect(() => {
    setNavElevation(active);
    return () => setNavElevation(false);
  }, [active]);

  // Resolve target element and compute rect when step or route changes
  useEffect(() => {
    if (!active) return;

    const step = WALKTHROUGH_STEPS[stepIndex];

    // Hide overlay/tooltip while resolving next step
    setStepReady(false);

    // Clean up previous target elevation
    if (prevTargetEl) {
      prevTargetEl.style.removeProperty("position");
      prevTargetEl.style.removeProperty("z-index");
      setPrevTargetEl(null);
    }

    // No target = centered modal (final step)
    if (!step.target) {
      setTargetRect(null);
      setStepReady(true);
      return;
    }

    let cancelled = false;

    const resolveTarget = async () => {
      // Navigate if needed
      if (!location.pathname.startsWith(step.route)) {
        navigate(step.route);
      }

      const el = await waitForElement(step.target!);
      if (cancelled || !el) {
        if (!cancelled) {
          setTargetRect(null);
          setStepReady(true);
        }
        return;
      }

      // Elevate target above overlay
      el.style.position = "relative";
      el.style.zIndex = "65";
      setPrevTargetEl(el);

      // Scroll into view if needed
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });

      // Wait for scroll to settle, then measure and show
      await new Promise((r) => setTimeout(r, 250));
      if (cancelled) return;

      setTargetRect(el.getBoundingClientRect());
      setStepReady(true);
    };

    resolveTarget();

    return () => {
      cancelled = true;
    };
  }, [active, stepIndex, location.pathname]);

  // Recalculate rect on resize/scroll
  useEffect(() => {
    if (!active || !stepReady) return;

    const step = WALKTHROUGH_STEPS[stepIndex];
    if (!step.target) return;

    const updateRect = () => {
      const el = findTarget(step.target!);
      if (el) setTargetRect(el.getBoundingClientRect());
    };

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [active, stepIndex, stepReady]);

  const handleComplete = useCallback(async () => {
    if (prevTargetEl) {
      prevTargetEl.style.removeProperty("position");
      prevTargetEl.style.removeProperty("z-index");
    }
    setPhase("completed");
    setStepIndex(0);
    setTargetRect(null);
    setPrevTargetEl(null);
    setStepReady(false);
    await setWalkthroughCompleted(true);
    navigate("/add");
  }, [setWalkthroughCompleted, navigate, prevTargetEl]);

  const handleNext = useCallback(() => {
    if (stepIndex < WALKTHROUGH_STEPS.length - 1) {
      setStepIndex((s) => s + 1);
    } else {
      handleComplete();
    }
  }, [stepIndex, handleComplete]);

  const handlePrev = useCallback(() => {
    if (stepIndex > 0) {
      setStepIndex((s) => s - 1);
    }
  }, [stepIndex]);

  return (
    <>
      {children}

      {/* Welcome prompt — ask before starting */}
      {phase === "prompt" &&
        createPortal(
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <motion.div
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border p-8 max-w-sm mx-4 text-center"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
            >
              <div className="text-4xl mb-3">&#x1F331;</div>
              <h2 className="text-xl font-bold mb-2">Quick Tour?</h2>
              <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                Want a 30-second walkthrough of Shoresh? We'll show you where everything lives so you can hit the ground running.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleComplete}
                >
                  Skip for Now
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => setPhase("active")}
                >
                  Show Me Around
                </Button>
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {/* Active walkthrough — only render when step target is resolved */}
      {active &&
        stepReady &&
        createPortal(
          <AnimatePresence mode="wait">
            <WalkthroughOverlay
              key="overlay"
              targetRect={targetRect}
              onClose={handleComplete}
            />
            <WalkthroughTooltip
              key={`tooltip-${stepIndex}`}
              step={WALKTHROUGH_STEPS[stepIndex]}
              targetRect={targetRect}
              currentIndex={stepIndex}
              totalSteps={WALKTHROUGH_STEPS.length}
              onNext={handleNext}
              onPrev={handlePrev}
              onSkip={handleComplete}
            />
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
